<template>
  <VanPopup
    v-model:show="store.showCharacterAllDialogModal"
    class="h-[80%] overflow-hidden"
    position="bottom"
    round
  >
    <div class="grid grid-rows-[auto_1fr] h-full">
      <div class="mb-4">
        <div class="relative">
          <h1 class="text-base text-center my-4">
            {{ $t("game.show.allDialog") }}
          </h1>
          <button
            class="btn btn-xs btn-primary absolute top-0 right-4"
            @click="clickRefresh"
          >
            <i class="iconfont icon-rotate-right text-xs"></i>
          </button>
        </div>
        <div class="flex gap-1 px-3">
          <div
            class="flex items-center justify-center"
            @click="showNodeSelect = true"
          >
            <div class="border px-2 py-1 rounded max-w-full flex">
              <span class="line-clamp-1">
                {{ $t("game.plotNode") }}：{{
                  store.activeStoryNodeId
                    ? store.nodeMap[store.activeStoryNodeId].name
                    : t(`Buttons.all`)
                }}
              </span>

              <i class="iconfont icon-down1 text-xs ml-1 mt-1"></i>
            </div>
          </div>
          <div
            class="flex items-center justify-center"
            @click="showCharacterSelect = true"
          >
            <div class="border px-2 py-1 rounded max-w-full flex">
              <span class="line-clamp-1">
                {{
                  store.activeCharacterId
                    ? getName(store.activeCharacterId)
                    : t(`Buttons.all`)
                }}
              </span>
              <i class="iconfont icon-down1 text-xs ml-1 mt-1"></i>
            </div>
          </div>
        </div>
      </div>

      <ThreadList
        ref="promptListRef"
        :enable-pull-load="false"
        :on-scroll-load="store.fetchCharacterAllDialog"
        class="px-2"
      >
        <div
          v-for="dialog in store.characterDialogList"
          class="flex items-center gap-1 mb-1"
        >
          <div :key="dialog.id" class="flex flex-col items-center">
            <div
              v-if="store.characterIdMap[dialog.characterId!].role === 4"
              class="border-2 border-[#3f3f3f] flex items-center justify-center w-[50px] aspect-[3/4] rounded"
            >
              <i class="iconfont icon-icon text-xl" />
            </div>

            <UserAvatar
              v-else
              :avatar-url="store.characterIdMap[dialog.characterId!].avatarMedia?.url"
              class="w-[50px] aspect-[3/4] rounded-sm"
            />
            <div class="flex items-center py-1 px-2 rounded">
              <span class="mr-1">
                {{ getName(dialog.characterId!) }}
              </span>
            </div>
          </div>
          <div class="flex-1 border rounded-md p-2.5 flex flex-col">
            <p class="flex-1">
              {{ dialog?.text }}
            </p>

            <!-- 管理 -->
            <div class="flex justify-end mt-4 font-light">
              <div class="flex items-center">
                <button
                  :class="[
                    'btn btn-primary btn-xs rounded border-none mr-2 px-1',
                    dialog.isAudioEnabled && 'text-primary',
                  ]"
                  @click="clickEditAudioEnabled(dialog)"
                >
                  <i class="iconfont icon-volume text-xs font-light" />
                </button>
                <button
                  v-if="dialog.text && dialog.mateToneId"
                  class="btn btn-primary btn-xs rounded border-none mr-2 px-1"
                  @click="clickReGenerateAudio(dialog)"
                >
                  <i
                    class="iconfont icon-switch text-xs mr-[-3px] font-light"
                  />
                  <span>{{ $t("Buttons.regenerate") }}</span>
                </button>
                <PlayAudio
                  v-if="
                    dialog.audioMedia?.url && store.showCharacterAllDialogModal
                  "
                  :url="dialog.audioMedia?.url"
                  class="mr-2"
                />
                <div
                  v-else-if="dialog.text && dialog.mateToneId"
                  class="flex text-xs items-center text-my-gray"
                >
                  {{ $t("game.processing") }}
                  <p class="text-xs loading loading-dots ml-1"></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ThreadList>
    </div>
  </VanPopup>
  <ModalSelectNodeGalGameEditor
    v-model:show="showNodeSelect"
    show-all
    @confirm="onNodeSelect"
  />

  <ModalSelectCharacterGalGameEditor
    v-model:show="showCharacterSelect"
    @confirm="onCharacterSelect"
  />
</template>

<script lang="ts" setup>
import { ApiPostGalGameDialogToneRegenerate } from "@/apis/gal-game";
import ThreadList, {
  ThreadListInstance,
} from "@/components/mobile/ThreadList.vue";
import { useGalGameEditor } from "@/stores/galGameEditor";
import { showFailToast, showLoadingToast, showSuccessToast } from "vant";
import { ref } from "vue";
import { watch } from "vue";
import { useI18n } from "vue-i18n";
import i18n from "@/locales";

const store = useGalGameEditor();
const { t } = useI18n();
const promptListRef = ref<ThreadListInstance>();

// 搜索条件
const showNodeSelect = ref(false);
const showCharacterSelect = ref(false);

const getName = (id: number) => {
  return (
    store.characterIdMap[id]?.name ||
    t(`game.role.${store.characterIdMap[id].role}`)
  );
};

const onNodeSelect = (targetNodeId: number) => {
  if (targetNodeId) store.activeStoryNodeId = targetNodeId;
  else store.activeStoryNodeId = undefined;
  clickRefresh();
  showNodeSelect.value = false;
};

const clickRefresh = () => {
  store.characterDialogList = [];
  promptListRef.value?.resetScrollLoadingState();
};

const onCharacterSelect = (targetCharacterId: number) => {
  if (targetCharacterId) store.activeCharacterId = targetCharacterId;
  else store.activeCharacterId = undefined;
  store.characterDialogList = [];
  promptListRef.value?.resetScrollLoadingState();
  showCharacterSelect.value = false;
};

watch(
  () => store.showCharacterAllDialogModal,
  (show) => {
    if (show) {
      promptListRef.value?.resetScrollLoadingState();
    }
  }
);

// 点击编辑事件
const clickEditAudioEnabled = (dialog: GalGameDialog) => {
  dialog.isAudioEnabled = !dialog.isAudioEnabled;
  store.saveDialog(dialog);
};
const clickReGenerateAudio = async (dialog: GalGameDialog) => {
  const loading = showLoadingToast({
    message: i18n.global.t("Message.regeneratingAudio"),
    duration: 0,
    mask: true,
  });

  try {
    if (dialog.audioMedia) dialog.audioMedia.url = "";
    const { item } = await ApiPostGalGameDialogToneRegenerate(dialog.id!);
    if (dialog.audioMedia) dialog.audioMedia.url = item.audioMedia.url;
    else dialog.audioMedia = item.audioMedia;
    loading.close();
    showSuccessToast(i18n.global.t("Message.regeneratedAudioSuccessfully"));
  } catch (e) {
    console.log(e);
    loading.close();
    showFailToast(i18n.global.t("Message.regeneratedAudioFailed"));
  }
};
</script>
